<script setup lang="ts">
/**
 * Типы задач — макет 84:404: две кнопки профиля, подпись про иерархию и список
 * типов с описанием, счётчиком и тумблером.
 *
 * Тумблеры не трогаются руками, и это не упрощение: набор типов задаёт профиль
 * проекта — так же говорит и подпись в макете. Своих типов в трекере нет вовсе,
 * поэтому переключатель здесь показывает, а не решает.
 *
 * Счётчик задач по типу — пять запросов по одной строке ради `total`. Дорого,
 * но без него переход на простой профиль выглядит безобидно, а он прячет с
 * доски эпики и истории, которые в проекте уже есть.
 */
import { AppSwitch } from '@vinprop/design-system'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import { listIssues, typesOfProfile } from '@/features/issues/api'
import { emptyFilters } from '@/features/issues/filters'

import { useSettingsStore } from '../store'

import type { V1IssueType } from '@/api/clients/tracker-issue'
import type { V1TypeProfile } from '@/api/clients/tracker-project'

/** Все типы трекера сверху вниз по иерархии — тот же порядок, что в макете */
const ALL_TYPES: V1IssueType[] = [
  'ISSUE_TYPE_EPIC',
  'ISSUE_TYPE_STORY',
  'ISSUE_TYPE_TASK',
  'ISSUE_TYPE_BUG',
  'ISSUE_TYPE_SUBTASK',
]

const PROFILES: V1TypeProfile[] = ['TYPE_PROFILE_DEVELOPMENT', 'TYPE_PROFILE_SIMPLE']

const { t } = useI18n()
const store = useSettingsStore()

const counts = ref<Partial<Record<V1IssueType, number>>>({})

const enabled = computed(() => new Set(typesOfProfile(store.form.typeProfile)))

const loadCounts = async () => {
  if (!store.project) return

  const projectId = store.project.id

  const loaded = await Promise.all(
    ALL_TYPES.map(async (type) => {
      try {
        const page = await listIssues(
          projectId,
          { ...emptyFilters(), types: [type] },
          { field: 'key', desc: false },
          1,
          1,
        )

        return [type, page.total] as const
      } catch {
        return [type, 0] as const
      }
    }),
  )

  counts.value = Object.fromEntries(loaded)
}

onMounted(loadCounts)

// Экран открывается раньше, чем приезжает проект: без этого счётчики остались
// бы нулями на первом же заходе по прямой ссылке.
watch(() => store.project?.id, loadCounts)
</script>

<template>
  <div class="ttab">
    <div class="ttab__profiles">
      <button
        v-for="profile in PROFILES"
        :key="profile"
        type="button"
        class="ttab__profile"
        :class="{ 'ttab__profile--active': store.form.typeProfile === profile }"
        @click="store.form.typeProfile = profile"
      >
        {{ t(`settings.types.profiles.${profile}`) }}
      </button>
    </div>

    <p class="ttab__hint">{{ t('settings.types.hint') }}</p>

    <div class="ttab__card">
      <div v-for="type in ALL_TYPES" :key="type" class="trow">
        <IssueTypeIcon :type="type" />

        <div class="trow__text">
          <span class="trow__name">{{ t(`issues.types.${type}`) }}</span>
          <span class="trow__note">{{ t(`settings.types.notes.${type}`) }}</span>
        </div>

        <span class="trow__spacer" />

        <span class="trow__count">{{ t('settings.types.count', counts[type] ?? 0) }}</span>

        <AppSwitch
          :model-value="enabled.has(type)"
          disabled
          :aria-label="t(`issues.types.${type}`)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.ttab {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Сегмент из двух кнопок: активная белая с тенью, как переключатель вида */
.ttab__profiles {
  display: inline-flex;
  gap: 2px;
  align-self: flex-start;
  padding: 3px;
  border-radius: 9px;
  background: var(--color-bg-muted);
}

.ttab__profile {
  padding: 6px 14px;
  border: none;
  border-radius: 7px;
  background: none;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
}

.ttab__profile--active {
  background: var(--color-surface);
  color: var(--color-text-primary);
  box-shadow: var(--shadow-sm);
}

.ttab__hint {
  margin: -12px 0 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.ttab__card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.trow {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 60px;
  padding: 0 16px;
  border-top: 1px solid var(--color-border);
}

.trow:first-child {
  border-top: none;
}

.trow__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.trow__name {
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
}

.trow__note {
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.trow__spacer {
  flex: 1;
}

.trow__count {
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
  font-variant-numeric: tabular-nums;
}
</style>
